import {
  isIframe,
  isScript,
} from '../tagUtils'

import makeCreateAttribute from '../makeCreateAttribute'
import makeSetAttribute from '../makeSetAttribute'

import augmentElementDataset from './augmentElementDataset'

/**
 * @param {Function} nativeCreateElement
 *
 * @returns {Function}
 */
export default function makeCreateElement(nativeCreateElement) {
  /**
   * An override for `document.createElement()` that overrides the
   * setters for the `type` and `src` attributes, with the goal being
   * to thwart efforts to load prohibited resources.
   *
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Document/createElement}
   *
   * @param {string} tagName
   * @param {object} [options]
   *
   * @returns {Element} the new element
   */
  // N.B.: this must be a regular function (not an arrow) so that
  // `this` reflects the document on which `createElement()` was
  // called. Using an arrow + `.bind(document)` forced every call
  // through the *HTML* document, which silently stamped elements
  // created on XML documents (e.g. DOMParser output used by
  // DataTables/JSZip) with the XHTML namespace, corrupting XLSX
  // exports. See TER-18398.
  return function(tagName, options) {
    const element = nativeCreateElement.call(this, tagName, options)

    if ( !isScript(element) && !isIframe(element) ) {
      return element
    }

    const augmentedElement = augmentElementDataset(element)

    augmentedElement.setAttribute = makeSetAttribute(augmentedElement)

    return Object.defineProperties(augmentedElement, {
      type: makeCreateAttribute('type'),
      src: makeCreateAttribute('src'),
    })
  }
}
